import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Button, CdnIcon, CheckboxWithLabel, DatePicker, Select, TRANSFORMER_MAX_MOBILE_WIDTH_PX, TravelerSelection, cn } from "@pcln/horizon";
import { addMonths } from "date-fns/addMonths";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useMediaQuery } from "react-responsive";
import { FormAutocompleteField, resolveAutocompleteFieldOptions } from "../shared/components/Autocomplete/FormAutocompleteField.js";
import { BundleAndSave } from "../shared/components/BundleAndSave.js";
import { SearchFormGrid } from "../shared/components/SearchFormGrid.js";
import { SubmitButton } from "../shared/components/SubmitButton.js";
import { DEFAULT_COLLISION_AVOIDANCE, FLY, STAY } from "../shared/constants.js";
import { useContainerBreakpoint } from "../shared/hooks/useContainerBreakpoint.js";
import { useTabGroup } from "../shared/hooks/useTabGroup.js";
import { generateTimeSelectOptions } from "../shared/utils/generate-time-options.js";
import { createYupResolver } from "../shared/validation/yup-form-resolver.js";
import { getGridContainerClass, getInputClass, getWideLayoutBreakpointPx } from "./CarSearchForm.variants.js";
import { LABELS } from "./constants.js";
import { handleDateRangeCalendarChange } from "./formHandlers.js";
import validation from "./validation.js";
const CAR_INITIAL_STATE = {
    travelers: {
        adults: 2,
        childrenAges: {},
        children: 0,
        rooms: 1,
        lapInfants: 0
    },
    endDate: '',
    endLocation: null,
    endTime: '12:00',
    hotelEndDate: '',
    hotelStartDate: '',
    oneWay: false,
    startDate: '',
    startLocation: null,
    startTime: '12:00',
    tripType: 'DRIVE'
};
function CarSearchForm({ onSubmit, initialValues, isMobile = false, renderAutocomplete, autocompleteOptions, localeCode = 'en-US', showBundleAndSave = true, removeBackground = false, highlightBundleAndSave = false, setLocation, onItemClick, autoAdvance = false, onBundleChange, submitButtonVariant = 'label', submitLabel = LABELS.SEARCH_BUTTON, defaultHotelDestinationFromPickup = false, singleRow = false, onBackButtonClick, backButtonLabel = LABELS.BACK_BUTTON }) {
    const mergedInitialValues = useMemo(()=>({
            ...CAR_INITIAL_STATE,
            ...initialValues
        }), [
        initialValues
    ]);
    const { registerTabElement, focusNextElement } = useTabGroup();
    const fieldRefs = useRef(new Map());
    const containerRef = useRef(null);
    const isWideLayout = useContainerBreakpoint(containerRef, getWideLayoutBreakpointPx(singleRow));
    const makeInputRef = useCallback((key)=>{
        if (!autoAdvance) return;
        return (el)=>{
            if (el) fieldRefs.current.set(key, el);
            registerTabElement(el);
        };
    }, [
        autoAdvance,
        registerTabElement
    ]);
    const { clearErrors, control, setValue, handleSubmit, watch, trigger, formState: { errors }, reset } = useForm({
        defaultValues: mergedInitialValues,
        mode: 'onSubmit',
        resolver: createYupResolver(validation),
        reValidateMode: 'onChange'
    });
    const watchTripType = useWatch({
        control,
        name: 'tripType',
        defaultValue: mergedInitialValues.tripType
    });
    const watchStartLocation = useWatch({
        control,
        name: 'startLocation'
    });
    const watchEndLocation = useWatch({
        control,
        name: 'endLocation'
    });
    const watchStartDate = useWatch({
        control,
        name: 'startDate'
    });
    const watchEndDate = useWatch({
        control,
        name: 'endDate'
    });
    const watchStartTime = useWatch({
        control,
        name: 'startTime',
        defaultValue: mergedInitialValues.startTime
    });
    const watchEndTime = useWatch({
        control,
        name: 'endTime',
        defaultValue: mergedInitialValues.endTime
    });
    const watchOneWay = useWatch({
        control,
        name: 'oneWay',
        defaultValue: mergedInitialValues.oneWay
    });
    const watchHotelStartDate = useWatch({
        control,
        name: 'hotelStartDate'
    });
    const watchHotelEndDate = useWatch({
        control,
        name: 'hotelEndDate'
    });
    const watchTravelers = useWatch({
        control,
        name: 'travelers',
        defaultValue: mergedInitialValues.travelers
    });
    useEffect(()=>{
        clearErrors('startLocation');
    }, [
        clearErrors,
        watchStartLocation
    ]);
    useEffect(()=>{
        clearErrors('endLocation');
    }, [
        clearErrors,
        watchEndLocation
    ]);
    useEffect(()=>{
        clearErrors('startDate');
        clearErrors('endTime');
    }, [
        clearErrors,
        watchStartDate,
        watchTripType
    ]);
    useEffect(()=>{
        clearErrors('endDate');
        clearErrors('endTime');
    }, [
        clearErrors,
        watchEndDate,
        watchTripType
    ]);
    useEffect(()=>{
        clearErrors('hotelStartDate');
    }, [
        clearErrors,
        watchHotelStartDate,
        watchTripType
    ]);
    useEffect(()=>{
        clearErrors('hotelEndDate');
    }, [
        clearErrors,
        watchHotelEndDate,
        watchTripType
    ]);
    useEffect(()=>{
        if (watchTravelers) {
            trigger('travelers.childrenAges');
            trigger('travelers.children');
            trigger('travelers.rooms');
        }
    }, [
        trigger,
        watchTripType,
        watchTravelers
    ]);
    useEffect(()=>{
        const subscription = watch((value)=>{
            const activeLocation = value.tripType?.includes(STAY) && !value.tripType.includes(FLY) ? value.endLocation : value.startLocation;
            if (setLocation && activeLocation) setLocation(activeLocation);
        });
        return ()=>subscription.unsubscribe();
    }, [
        watch,
        setLocation
    ]);
    const resetFormToInitialValues = useCallback((overrides)=>{
        clearErrors();
        reset({
            ...mergedInitialValues,
            ...overrides
        });
    }, [
        clearErrors,
        reset,
        mergedInitialValues
    ]);
    const dropoffDatePickerTriggerRef = useRef(null);
    const dropoffDatePickerTimeoutRef = useRef(null);
    useEffect(()=>()=>{
            if (dropoffDatePickerTimeoutRef.current) clearTimeout(dropoffDatePickerTimeoutRef.current);
        }, []);
    const isMobileComponent = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const currentDate = new Date();
    const calendarMaxMonth = addMonths(currentDate, 11);
    const [defaultRangeMonth, setDefaultRangeMonth] = useState(currentDate);
    const [defaultPickupMonth, setDefaultPickupMonth] = useState(currentDate);
    const [defaultDropoffMonth, setDefaultDropoffMonth] = useState(currentDate);
    const [bundleSelections, setBundleSelections] = useState({
        STAY: false,
        FLY: false
    });
    const isHotel = bundleSelections.STAY;
    const isFlight = bundleSelections.FLY;
    const isPackage = watchTripType.includes('STAY') || watchTripType.includes('FLY');
    const onCalendarChange = useCallback((dates)=>{
        handleDateRangeCalendarChange(setValue, setDefaultRangeMonth, dates);
    }, [
        setValue
    ]);
    const onSingleDateChange = useCallback((date)=>{
        const formattedDate = format(date, 'yyyyMMdd');
        setValue('startDate', formattedDate, {
            shouldDirty: true
        });
        setDefaultPickupMonth(date);
        setDefaultDropoffMonth(date);
        const hasDateRangeError = watchEndDate && formattedDate > watchEndDate;
        if (hasDateRangeError) trigger('endDate');
        if (isMobile || hasDateRangeError) {
            if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        } else {
            if (dropoffDatePickerTimeoutRef.current) clearTimeout(dropoffDatePickerTimeoutRef.current);
            dropoffDatePickerTimeoutRef.current = setTimeout(()=>{
                dropoffDatePickerTimeoutRef.current = null;
                dropoffDatePickerTriggerRef.current?.click();
            }, 0);
        }
    }, [
        setValue,
        watchEndDate,
        isMobile,
        trigger
    ]);
    const onEndDateChange = useCallback((date)=>{
        const formattedDate = format(date, 'yyyyMMdd');
        setValue('endDate', formattedDate, {
            shouldDirty: true
        });
        setDefaultDropoffMonth(date);
        if (watchStartDate && formattedDate === watchStartDate) trigger('endTime');
    }, [
        setValue,
        trigger,
        watchStartDate
    ]);
    const onEndDateTimeChange = useCallback((time)=>{
        setValue('endTime', time, {
            shouldDirty: true
        });
        trigger('endTime');
    }, [
        setValue,
        trigger
    ]);
    const onStartTimeChange = useCallback((time)=>{
        setValue('startTime', time);
        if (watchStartDate && watchEndDate && watchStartDate === watchEndDate) trigger('endTime');
    }, [
        setValue,
        trigger,
        watchStartDate,
        watchEndDate
    ]);
    const onItemSelect = useCallback((item, searchKey)=>{
        setValue(searchKey, item);
        onItemClick?.();
        if (!isMobile && item && autoAdvance) {
            const fromEl = fieldRefs.current.get(searchKey);
            focusNextElement(true, fromEl);
        } else if (isMobile && !!item && document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }, [
        isMobile,
        setValue,
        onItemClick,
        autoAdvance,
        focusNextElement
    ]);
    const handleSubmitHelper = handleSubmit((data)=>{
        onSubmit(data);
    });
    const handleTravelerChange = (travelerInfo)=>{
        setValue('travelers', travelerInfo);
    };
    const handleBundleSelectionChange = useCallback((updatedTripType, checked, optionValue)=>{
        setBundleSelections((prev)=>({
                ...prev,
                [optionValue]: checked
            }));
        let nextStartLocation = watchStartLocation;
        let nextEndLocation = watchEndLocation;
        if (defaultHotelDestinationFromPickup) {
            nextEndLocation = null;
            if (updatedTripType.includes(FLY)) if (checked && optionValue === FLY) {
                nextEndLocation = watchStartLocation;
                nextStartLocation = null;
            } else nextEndLocation = watchEndLocation;
            else if (updatedTripType.includes(STAY)) if (watchTripType.includes(FLY)) {
                nextStartLocation = watchEndLocation;
                nextEndLocation = watchEndLocation;
            } else nextEndLocation = watchStartLocation;
            else if (watchTripType.includes(FLY)) nextStartLocation = watchEndLocation;
        }
        resetFormToInitialValues({
            tripType: updatedTripType,
            startLocation: nextStartLocation,
            endLocation: nextEndLocation,
            startDate: watchStartDate,
            endDate: watchEndDate,
            startTime: watchStartTime,
            endTime: watchEndTime
        });
        onBundleChange?.(updatedTripType, checked, optionValue);
    }, [
        resetFormToInitialValues,
        watchStartLocation,
        watchEndLocation,
        watchStartDate,
        watchEndDate,
        watchStartTime,
        watchEndTime,
        onBundleChange,
        defaultHotelDestinationFromPickup,
        watchTripType
    ]);
    const handleDifferentDropOffChange = useCallback(()=>{
        const newValue = !watchOneWay;
        setValue('oneWay', newValue, {
            shouldDirty: true
        });
        resetFormToInitialValues({
            oneWay: newValue,
            startLocation: watchStartLocation,
            endLocation: watchEndLocation,
            startDate: watchStartDate,
            endDate: watchEndDate,
            startTime: watchStartTime,
            endTime: watchEndTime
        });
    }, [
        setValue,
        resetFormToInitialValues,
        watchOneWay,
        watchEndDate,
        watchEndTime,
        watchStartDate,
        watchStartTime,
        watchStartLocation,
        watchEndLocation
    ]);
    const timeOptions = useMemo(()=>generateTimeSelectOptions(localeCode), [
        localeCode
    ]);
    const isIconSubmitButton = 'icon' === submitButtonVariant;
    const primaryLocationSearchKey = isHotel ? 'endLocation' : 'startLocation';
    const primaryLocation = isHotel ? watchEndLocation : watchStartLocation;
    const hasBackButton = singleRow && !!onBackButtonClick;
    const backButton = hasBackButton && /*#__PURE__*/ jsx(Button, {
        buttonType: "button",
        className: getInputClass('back'),
        iconLeft: "chevron_left",
        onClick: onBackButtonClick,
        type: "tertiary",
        children: backButtonLabel
    });
    const optionsCheckbox = /*#__PURE__*/ jsx("div", {
        className: cn(getInputClass('options', watchOneWay, isHotel, isFlight, singleRow), 'py-2'),
        children: /*#__PURE__*/ jsx(CheckboxWithLabel, {
            size: "16",
            name: "differentDropOff",
            id: "differentDropOff",
            value: "differentDropOff",
            checked: watchOneWay,
            handleChange: handleDifferentDropOffChange,
            disabled: isPackage,
            labelNode: /*#__PURE__*/ jsx("span", {
                children: LABELS.DIFFERENT_DROPOFF
            }),
            labelClassName: "ml-2"
        })
    });
    const bundleAndSaveSection = showBundleAndSave && /*#__PURE__*/ jsx("div", {
        className: cn(getInputClass('package', watchOneWay, isHotel, isFlight, singleRow)),
        children: /*#__PURE__*/ jsx(BundleAndSave, {
            productType: "cars",
            checkedValues: bundleSelections,
            onSelectionChange: handleBundleSelectionChange,
            tripType: watchTripType,
            removeBackground: removeBackground,
            highlightBackground: highlightBundleAndSave
        })
    });
    return /*#__PURE__*/ jsxs(SearchFormGrid, {
        gridClassName: getGridContainerClass(watchOneWay, isHotel, isFlight, singleRow, hasBackButton),
        ariaLabel: "horizon-car-search-form",
        onSubmit: handleSubmitHelper,
        containerRef: containerRef,
        children: [
            isWideLayout && optionsCheckbox,
            backButton,
            isFlight ? /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: getInputClass('flightInput'),
                        children: /*#__PURE__*/ jsx(FormAutocompleteField, {
                            ...resolveAutocompleteFieldOptions(autocompleteOptions, {
                                searchKey: 'startLocation',
                                typeAheadEndpoint: 'flights'
                            }),
                            renderAutocomplete: renderAutocomplete,
                            searchKey: "startLocation",
                            label: "Where from?",
                            typeAheadEndpoint: "flights",
                            errorMessage: errors.startLocation?.message,
                            initialSelectedItem: watchStartLocation,
                            onItemSelect: (itemSelected)=>{
                                onItemSelect(itemSelected, 'startLocation');
                            },
                            inputRef: makeInputRef('startLocation'),
                            collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                        })
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: getInputClass('flightInput'),
                        children: /*#__PURE__*/ jsx(FormAutocompleteField, {
                            ...resolveAutocompleteFieldOptions(autocompleteOptions, {
                                searchKey: 'endLocation',
                                typeAheadEndpoint: 'flights'
                            }),
                            renderAutocomplete: renderAutocomplete,
                            searchKey: "endLocation",
                            label: "Where to?",
                            typeAheadEndpoint: "flights",
                            errorMessage: errors.endLocation?.message,
                            initialSelectedItem: watchEndLocation,
                            onItemSelect: (itemSelected)=>{
                                onItemSelect(itemSelected, 'endLocation');
                            },
                            inputRef: makeInputRef('endLocation'),
                            collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                        })
                    })
                ]
            }) : /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: getInputClass('pickup', watchOneWay, isHotel, isFlight, singleRow),
                        children: /*#__PURE__*/ jsx(FormAutocompleteField, {
                            ...resolveAutocompleteFieldOptions(autocompleteOptions, {
                                searchKey: primaryLocationSearchKey,
                                typeAheadEndpoint: 'cars'
                            }),
                            renderAutocomplete: renderAutocomplete,
                            searchKey: primaryLocationSearchKey,
                            label: LABELS.DEFAULT_LOCATION,
                            typeAheadEndpoint: "cars",
                            errorMessage: isHotel ? errors.endLocation?.message : errors.startLocation?.message,
                            initialSelectedItem: primaryLocation,
                            onItemSelect: (itemSelected)=>{
                                onItemSelect(itemSelected, primaryLocationSearchKey);
                            },
                            inputRef: makeInputRef(primaryLocationSearchKey),
                            collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                        })
                    }, primaryLocationSearchKey),
                    watchOneWay && !isPackage && /*#__PURE__*/ jsx("div", {
                        className: getInputClass('dropoff', watchOneWay, isHotel, isFlight, singleRow),
                        children: /*#__PURE__*/ jsx(FormAutocompleteField, {
                            ...resolveAutocompleteFieldOptions(autocompleteOptions, {
                                searchKey: 'endLocation',
                                typeAheadEndpoint: 'cars'
                            }),
                            renderAutocomplete: renderAutocomplete,
                            searchKey: "endLocation",
                            label: LABELS.DEFAULT_LOCATION,
                            typeAheadEndpoint: "cars",
                            errorMessage: errors.endLocation?.message,
                            initialSelectedItem: watchEndLocation,
                            onItemSelect: (itemSelected)=>{
                                onItemSelect(itemSelected, 'endLocation');
                            },
                            inputRef: makeInputRef('endLocation'),
                            collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                        })
                    })
                ]
            }),
            !isWideLayout && optionsCheckbox,
            isPackage ? /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: getInputClass('packageDatePickerWrapper'),
                        children: /*#__PURE__*/ jsx(DatePicker, {
                            errorMessage: errors.hotelEndDate?.message || errors.hotelStartDate?.message,
                            className: "flight-dates",
                            title: "Departing – Returning",
                            label: "Departing – Returning",
                            mode: "range",
                            numberOfMonths: isMobileComponent ? 12 : 2,
                            handleCalendarChange: onCalendarChange,
                            defaultMonth: defaultRangeMonth,
                            disabled: {
                                before: currentDate
                            },
                            startMonth: currentDate,
                            endMonth: calendarMaxMonth,
                            min: isHotel ? 1 : 0,
                            inputRef: autoAdvance ? registerTabElement : void 0,
                            collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                        }, `package-dates-${watchTripType}`)
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: getInputClass('packageDatePickerWrapper'),
                        children: /*#__PURE__*/ jsx(TravelerSelection, {
                            isHotel: isHotel && !isFlight,
                            initialValues: mergedInitialValues.travelers,
                            roomsEnabled: isHotel,
                            maxRooms: 4,
                            maxChildren: 6,
                            maxTravelers: 8,
                            ageSelectEnabled: true,
                            tooltipErrorMessageId: "travelerSelectionError",
                            errorMessage: errors?.travelers?.childrenAges?.message || errors?.travelers?.children?.message || errors?.travelers?.rooms?.message || '',
                            onChange: (values)=>{
                                handleTravelerChange(values);
                            },
                            collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE,
                            disclaimer: "You may only book a package for up to 8 travelers."
                        })
                    })
                ]
            }) : /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: getInputClass('datePickerWrapper'),
                        children: /*#__PURE__*/ jsx(DatePicker, {
                            errorMessage: errors.startDate?.message,
                            label: LABELS.PICKUP_DATE,
                            mode: "single",
                            numberOfMonths: isMobileComponent ? 12 : 1,
                            selected: watchStartDate ? parse(watchStartDate, 'yyyyMMdd', currentDate) : void 0,
                            handleCalendarChange: onSingleDateChange,
                            disabled: {
                                before: currentDate
                            },
                            startMonth: currentDate,
                            endMonth: calendarMaxMonth,
                            defaultMonth: defaultPickupMonth,
                            inputRef: autoAdvance ? registerTabElement : void 0,
                            collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                        }, `pickup-date-${watchTripType}`)
                    }),
                    /*#__PURE__*/ jsx(Select, {
                        label: LABELS.PICKUP_TIME,
                        isInset: true,
                        menuItems: timeOptions,
                        menuWidth: "150px",
                        initialValue: watchStartTime,
                        onChange: onStartTimeChange,
                        placeholder: LABELS.PICKUP_TIME
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: getInputClass('datePickerWrapper'),
                        children: /*#__PURE__*/ jsx(DatePicker, {
                            errorMessage: errors.endDate?.message,
                            label: LABELS.DROPOFF_DATE,
                            mode: "single",
                            numberOfMonths: isMobileComponent ? 12 : 1,
                            selected: watchEndDate ? parse(watchEndDate, 'yyyyMMdd', currentDate) : void 0,
                            handleCalendarChange: onEndDateChange,
                            disabled: {
                                before: watchStartDate ? parse(watchStartDate, 'yyyyMMdd', currentDate) : currentDate
                            },
                            startMonth: watchStartDate ? parse(watchStartDate, 'yyyyMMdd', currentDate) : currentDate,
                            endMonth: calendarMaxMonth,
                            defaultMonth: defaultDropoffMonth,
                            inputRef: (el)=>{
                                dropoffDatePickerTriggerRef.current = el;
                            },
                            collisionAvoidance: DEFAULT_COLLISION_AVOIDANCE
                        }, `dropoff-date-${watchTripType}-${watchStartDate}`)
                    }),
                    /*#__PURE__*/ jsx(Select, {
                        label: LABELS.DROPOFF_TIME,
                        isInset: true,
                        menuItems: timeOptions,
                        menuWidth: "150px",
                        initialValue: watchEndTime,
                        onChange: onEndDateTimeChange,
                        placeholder: LABELS.DROPOFF_TIME,
                        isError: !!errors.endTime?.message,
                        errorTooltipText: errors.endTime?.message
                    })
                ]
            }),
            !isWideLayout && bundleAndSaveSection,
            /*#__PURE__*/ jsx(SubmitButton, {
                className: cn(getInputClass('submit', watchOneWay, isHotel, isFlight, singleRow), !showBundleAndSave && !isPackage && (singleRow ? '@5xl:col-start-auto' : '@3xl:col-start-3 @7xl:col-start-auto'), isPackage && !singleRow && '@3xl:col-span-1', isIconSubmitButton && !isMobile && 'min-w-[72px] px-0'),
                isMobile: isMobile,
                children: isIconSubmitButton ? /*#__PURE__*/ jsxs(Fragment, {
                    children: [
                        /*#__PURE__*/ jsx(CdnIcon, {
                            iconName: "search",
                            size: "24",
                            "aria-hidden": true,
                            "data-testid": "submit-search-icon"
                        }),
                        /*#__PURE__*/ jsx("span", {
                            className: "sr-only",
                            children: submitLabel
                        })
                    ]
                }) : submitLabel
            }),
            isWideLayout && bundleAndSaveSection
        ]
    });
}
export { CarSearchForm };
